import { FIRESTORE_MIGRATION_BATCH } from './firestoreLimits';
import { getDocs, writeBatch, setDoc, deleteDoc } from 'firebase/firestore';
import {
  db,
  studentSubCol,
  studentSubDoc,
  studentDoc,
  STUDENT_SUBCOLLECTIONS,
} from '../firebase/config';
import { normalizeEmail, emailsMatch } from './emailUtils';
import { normalizePhoneTR } from './phoneUtils';

// ─── Eğitmen filtreleri ─────────────────────────────────────────────────────

export const filterMyStudents = (students, instructorRef) => {
  if (!students?.length) return [];
  const email = typeof instructorRef === 'string' ? instructorRef : instructorRef?.emailOrPhone;
  const id = typeof instructorRef === 'object' ? instructorRef?.id : null;
  return students.filter(
    (s) => (id && s.instructorId === id) || emailsMatch(s.instructorEmail, email)
  );
};

/** Eklenme zamanı olmayan eski kayıtlar sona, kendi aralarında ada göre dizilir */
const addedAt = (s) => Number(s.createdAt) || Number(s.consentGivenAt) || 0;

export const sortStudentsNewestFirst = (students) =>
  [...(students || [])].sort((a, b) =>
    (addedAt(b) - addedAt(a)) || (a.name || '').localeCompare(b.name || '', 'tr'));

export const sortStudentsByName = (students) =>
  [...(students || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));

/** Program belgelerinde updatedAt her yazımda tutulmadığı için içerik karşılaştırılır */
export const sameProgramList = (prev, next) => (
  Array.isArray(prev)
  && Array.isArray(next)
  && prev.length === next.length
  && prev.every((p, i) => p.id === next[i].id && JSON.stringify(p) === JSON.stringify(next[i]))
);

/** Aynı e-postalı çift kayıtlarda Firebase Auth UID'li belgeyi tercih et */
export const dedupeStudentsByEmailPreferUid = (students, authUid) => {
  const byEmail = new Map();
  students.forEach((student) => {
    const key = normalizeEmail(student.emailOrPhone)
      || normalizePhoneTR(student.emailOrPhone)
      || student.id;
    if (!key) return;
    const existing = byEmail.get(key);
    if (!existing) {
      byEmail.set(key, student);
      return;
    }
    const preferIncoming = student.id === authUid;
    const primary = preferIncoming ? student : existing;
    const secondary = preferIncoming ? existing : student;
    byEmail.set(key, {
      ...secondary,
      ...primary,
      // İletişim alanlarını birleştir — telefonsuz kopya telefonsuz kazanmasın
      phoneNumber: primary.phoneNumber || secondary.phoneNumber || '',
      emailOrPhone: primary.emailOrPhone || secondary.emailOrPhone || '',
    });
  });
  return Array.from(byEmail.values());
};

// ─── UID migrasyonu ─────────────────────────────────────────────────────────

/**
 * Çift öğrenci belgelerinden tek profil oluşturur.
 * Eğitmenin eklediği belgedeki atama alanları korunur.
 */
export const mergeStudentProfileDocs = (docs, uid) => {
  const allData = docs.map((d) => ({ id: d.id, ...d.data() }));
  const uidData = allData.find((d) => d.id === uid);
  const instructorAssigned = allData.find(
    (d) => d.instructorEmail && d.id !== uid
  ) || allData.find((d) => d.instructorEmail);

  const richest = allData.reduce((best, d) => {
    const score = Number(d.age) || 0;
    const bestScore = Number(best.age) || 0;
    return score > bestScore ? d : best;
  }, allData[0]);

  const base = uidData || instructorAssigned || richest || allData[0];
  const merged = { ...base };

  if (instructorAssigned) {
    merged.instructorId = instructorAssigned.instructorId ?? merged.instructorId ?? null;
    merged.instructorEmail = instructorAssigned.instructorEmail
      ? normalizeEmail(instructorAssigned.instructorEmail)
      : merged.instructorEmail ?? null;
    merged.status = instructorAssigned.status || merged.status;
    merged.age = instructorAssigned.age || merged.age || 0;
    merged.parent = instructorAssigned.parent || merged.parent || '-';
    merged.userCode = merged.userCode || instructorAssigned.userCode;
    merged.name = merged.name || instructorAssigned.name;
  }

  merged.emailOrPhone = normalizeEmail(merged.emailOrPhone || allData[0]?.emailOrPhone);
  merged.role = merged.role || 'student';
  merged.id = uid;
  return merged;
};

export const tasimaOgrenciAltVerileri = async (eskiId, yeniId) => {
  if (!eskiId || !yeniId || eskiId === yeniId) return;

  for (const sub of STUDENT_SUBCOLLECTIONS) {
    const snap = await getDocs(studentSubCol(eskiId, sub));
    if (snap.empty) continue;

    const docs = snap.docs;
    for (let i = 0; i < docs.length; i += FIRESTORE_MIGRATION_BATCH) {
      const batch = writeBatch(db);
      docs.slice(i, i + FIRESTORE_MIGRATION_BATCH).forEach((d) => {
        batch.set(studentSubDoc(yeniId, sub, d.id), d.data());
        batch.delete(d.ref);
      });
      await batch.commit();
    }
  }
};

export const birlestirOgrenciBelgeleri = async (uid, eskiBelgeler) => {
  const merged = mergeStudentProfileDocs(eskiBelgeler, uid);
  const { id, ...profileFields } = merged;
  await setDoc(studentDoc(uid), profileFields, { merge: true });

  const digerleri = eskiBelgeler.filter((d) => d.id !== uid);
  for (const eski of digerleri) {
    await tasimaOgrenciAltVerileri(eski.id, uid);
    await deleteDoc(eski.ref);
  }
  return merged;
};
